import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { AlertCircle, CheckCircle, X } from 'lucide-react';

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const ToastWrapper = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  left: 2rem;
  z-index: 9999;
  pointer-events: none;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 480px) {
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
    justify-content: center;
  }
`;

const ToastContainer = styled.div<{ $type: 'success' | 'error' }>`
  padding: 1rem 1.5rem;
  border-radius: 12px;
  background: ${({ $type }) =>
    $type === 'success'
      ? 'rgba(0, 255, 0, 0.95)'
      : 'rgba(255, 0, 0, 0.95)'};
  border: 1px solid ${({ $type }) =>
    $type === 'success'
      ? '#00ff00'
      : '#ff0000'};
  color: ${({ $type }) =>
    $type === 'success'
      ? '#000000'
      : '#ffffff'};
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 280px;
  max-width: 500px;
  animation: ${slideIn} 0.3s ease-out;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  pointer-events: auto;

  @media (max-width: 480px) {
    min-width: unset;
    max-width: 100%;
    width: 100%;
    padding: 0.875rem 1rem;
    font-size: 0.9rem;
  }
`;

const Message = styled.div`
  flex: 1;
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 500;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
  autoClose?: boolean;
}

export const Toast: React.FC<ToastProps> = ({
  message,
  type,
  onClose,
  autoClose = true,
}) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose]);

  return (
    <ToastWrapper>
      <ToastContainer $type={type}>
        {type === 'success' ? (
          <CheckCircle size={20} />
        ) : (
          <AlertCircle size={20} />
        )}
        <Message>{message}</Message>
        <CloseButton onClick={onClose} aria-label="Close notification">
          <X size={16} />
        </CloseButton>
      </ToastContainer>
    </ToastWrapper>
  );
};
